// In-app announcement popups — client side.
// Announcements are authored in the Jurident Admin portal (jradmin) and stored
// in Firestore. This service decides which one (if any) to show the current
// user on the dashboard, and records seen/dismissed in `announcementViews`.

import {
  collection, query, where, getDocs, doc, getDoc, setDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const ANNOUNCEMENTS = "announcements";
const ANN_VIEWS = "announcementViews";

// Replaces {{name}}, {{email}} etc. with per-user values.
export function applyAnnouncementVars(text, vars) {
  if (!text) return "";
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => vars?.[key] ?? "");
}

// Audience modes mirror the admin portal: all / lawyers / clients / specific.
// For `specific`, admin stores ids as "collection:uid" where collection is
// either "lawyers" or "client".
function audienceMatches(audience, userType, uid) {
  if (!audience || !audience.mode) return false;
  switch (audience.mode) {
    case "all": return true;
    case "lawyers": return userType === "lawyers";
    case "clients": return userType === "client";
    case "specific": {
      const coll = userType === "client" ? "client" : "lawyers";
      return Array.isArray(audience.userIds) && audience.userIds.includes(`${coll}:${uid}`);
    }
    default: return false;
  }
}

function withinWindow(a, now) {
  const start = a.startAt?.toDate ? a.startAt.toDate().getTime() : null;
  const end = a.endAt?.toDate ? a.endAt.toDate().getTime() : null;
  if (start != null && now < start) return false;
  if (end != null && now >= end) return false;
  return true;
}

// Frequency gating:
//  - once          → show a single time, ever (gated by the view doc existing)
//  - everyLogin    → once per browser session (gated by sessionStorage)
//  - untilDismissed→ every dashboard visit until the user dismisses it
async function isEligibleToShow(a, uid) {
  if (a.frequency === "everyLogin") {
    return sessionStorage.getItem(`ann_shown_${a.id}`) == null;
  }
  const viewSnap = await getDoc(doc(db, ANN_VIEWS, `${a.id}_${uid}`));
  if (a.frequency === "once") return !viewSnap.exists();
  // untilDismissed
  if (!viewSnap.exists()) return true;
  return !viewSnap.data().dismissedAt;
}

// Returns the single highest-priority announcement the user should see now,
// or null. Safe to call on every dashboard mount.
export async function getActiveAnnouncementForUser(uid, userType) {
  if (!uid) return null;
  const now = Date.now();
  const snap = await getDocs(
    query(collection(db, ANNOUNCEMENTS), where("active", "==", true)),
  );

  const candidates = [];
  snap.forEach((d) => {
    const a = { id: d.id, ...d.data() };
    if (!withinWindow(a, now)) return;
    if (!audienceMatches(a.audience, userType, uid)) return;
    candidates.push(a);
  });

  // Highest priority first; ties broken by most recently created.
  candidates.sort((x, y) => {
    const p = (y.priority || 0) - (x.priority || 0);
    if (p !== 0) return p;
    const xc = x.createdAt?.toMillis ? x.createdAt.toMillis() : 0;
    const yc = y.createdAt?.toMillis ? y.createdAt.toMillis() : 0;
    return yc - xc;
  });

  for (const a of candidates) {
    try {
      if (await isEligibleToShow(a, uid)) return a;
    } catch (err) {
      console.warn("announcement eligibility check failed:", err?.message || err);
    }
  }
  return null;
}

// Records that the user has seen the announcement. The admin "seen by N"
// stat counts these docs (one per user per announcement).
export async function markAnnouncementSeen(a, uid) {
  if (!a || !uid) return;
  if (a.frequency === "everyLogin") {
    try { sessionStorage.setItem(`ann_shown_${a.id}`, "1"); } catch { /* ignore */ }
  }
  try {
    await setDoc(
      doc(db, ANN_VIEWS, `${a.id}_${uid}`),
      { announcementId: a.id, uid, seenAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    console.warn("markAnnouncementSeen failed:", err?.message || err);
  }
}

// Clears the per-session "shown" flags used to gate `everyLogin`
// announcements. `sessionStorage` is tied to the browser tab, not the login,
// so this must be called on logout — otherwise logging out and back in within
// the same tab would not re-show an `everyLogin` announcement.
export function clearAnnouncementSessionFlags() {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("ann_shown_")) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch (err) {
    console.warn("clearAnnouncementSessionFlags failed:", err?.message || err);
  }
}

// Records an explicit dismissal — stops `untilDismissed` announcements from
// reappearing on the next visit.
export async function markAnnouncementDismissed(a, uid) {
  if (!a || !uid) return;
  try {
    await setDoc(
      doc(db, ANN_VIEWS, `${a.id}_${uid}`),
      { announcementId: a.id, uid, dismissedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    console.warn("markAnnouncementDismissed failed:", err?.message || err);
  }
}
